class FoundryTestimonialCarousel {
  static MOBILE_BREAKPOINT = 768;
  static AUTO_SCROLL_INTERVAL_MS = 3500;
  static SCROLL_ANIMATION_MS = 600;
  static USER_INTERACTION_RESUME_DELAY_MS = 3000;
  static RESIZE_DEBOUNCE_MS = 250;
  static SCROLL_TRACKING_DEBOUNCE_MS = 100;

  #section;
  #track;
  #items;
  #indicatorsContainer;
  #indicators = [];
  #currentIndex = 0;
  #isAutoScrollEnabled = true;
  #autoScrollTimerId = null;
  #isTransitioning = false;
  #abortController = new AbortController();
  #reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  constructor(section) {
    this.#section = section;
    this.#track = section.querySelector('[data-testimonials-track]');
    this.#items = Array.from(this.#track.querySelectorAll('[data-testimonial-item]'));
    this.#indicatorsContainer = section.querySelector('[data-scroll-indicators]');

    this.#buildIndicators();
    this.#bindListeners();
    this.#applyViewportLayout();
  }

  destroy() {
    this.#stopAutoScroll();
    this.#abortController.abort();
  }

  get #isMobile() {
    return window.innerWidth < FoundryTestimonialCarousel.MOBILE_BREAKPOINT;
  }

  get #prefersReducedMotion() {
    return this.#reducedMotionMediaQuery.matches;
  }

  #buildIndicators() {
    this.#indicatorsContainer.replaceChildren();
    this.#indicators = this.#items.map((_, itemIndex) => {
      const indicator = document.createElement('button');
      indicator.className = 'scroll-indicator';
      indicator.type = 'button';
      indicator.dataset.indicator = String(itemIndex);
      indicator.setAttribute('aria-label', `Go to testimonial ${itemIndex + 1}`);
      this.#indicatorsContainer.appendChild(indicator);
      return indicator;
    });
  }

  #updateActiveIndicator(rawIndex) {
    const activeIndex = rawIndex % this.#items.length;
    this.#indicators.forEach((indicator, indicatorIndex) => {
      indicator.classList.toggle('active', indicatorIndex === activeIndex);
    });
  }

  #cloneItemsForInfiniteLoop() {
    this.#track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
    if (this.#items.length === 0) return;

    for (let cloneSet = 0; cloneSet < 2; cloneSet++) {
      this.#items.forEach((item, originalIndex) => {
        const clone = item.cloneNode(true);
        clone.dataset.clone = 'true';
        clone.dataset.cloneIndex = String(originalIndex);
        clone.removeAttribute('data-testimonial-item');
        this.#track.appendChild(clone);
      });
    }
  }

  #scrollToTestimonial(index, useSmoothScrolling = true) {
    if (this.#isTransitioning) return;

    const cards = this.#track.children;
    const targetCard = cards[index];
    if (!targetCard) return;

    this.#isTransitioning = true;
    const shouldAnimate = useSmoothScrolling && !this.#prefersReducedMotion;

    this.#track.scrollTo({
      left: targetCard.offsetLeft - (this.#track.offsetWidth - targetCard.offsetWidth) / 2,
      behavior: shouldAnimate ? 'smooth' : 'auto',
    });

    setTimeout(() => {
      this.#isTransitioning = false;
    }, shouldAnimate ? FoundryTestimonialCarousel.SCROLL_ANIMATION_MS : 0);
  }

  #startAutoScroll() {
    if (!this.#isMobile || this.#items.length === 0 || this.#prefersReducedMotion) return;

    this.#stopAutoScroll();
    this.#autoScrollTimerId = setInterval(() => {
      if (!this.#isAutoScrollEnabled || this.#isTransitioning) return;

      this.#currentIndex++;
      const reachedEndOfClones = this.#currentIndex >= this.#items.length * 2;

      if (reachedEndOfClones) {
        this.#scrollToTestimonial(this.#currentIndex, true);
        setTimeout(() => {
          this.#currentIndex = 0;
          this.#scrollToTestimonial(0, false);
          this.#updateActiveIndicator(0);
        }, FoundryTestimonialCarousel.SCROLL_ANIMATION_MS);
      } else {
        this.#scrollToTestimonial(this.#currentIndex, true);
        this.#updateActiveIndicator(this.#currentIndex);
      }
    }, FoundryTestimonialCarousel.AUTO_SCROLL_INTERVAL_MS);
  }

  #stopAutoScroll() {
    if (this.#autoScrollTimerId !== null) {
      clearInterval(this.#autoScrollTimerId);
      this.#autoScrollTimerId = null;
    }
  }

  #pauseAutoScroll() {
    this.#isAutoScrollEnabled = false;
    this.#stopAutoScroll();
  }

  #resumeAutoScrollAfterDelay() {
    setTimeout(() => {
      this.#isAutoScrollEnabled = true;
      this.#startAutoScroll();
    }, FoundryTestimonialCarousel.USER_INTERACTION_RESUME_DELAY_MS);
  }

  #bindListeners() {
    const { signal } = this.#abortController;

    this.#indicators.forEach((indicator, indicatorIndex) => {
      indicator.addEventListener('click', () => {
        this.#pauseAutoScroll();
        this.#currentIndex = indicatorIndex;
        this.#scrollToTestimonial(indicatorIndex, true);
        this.#updateActiveIndicator(indicatorIndex);
        this.#resumeAutoScrollAfterDelay();
      }, { signal });
    });

    this.#track.addEventListener('touchstart', () => this.#pauseAutoScroll(), { passive: true, signal });
    this.#track.addEventListener('touchend', () => this.#resumeAutoScrollAfterDelay(), { passive: true, signal });
    this.#track.addEventListener('mousedown', () => this.#pauseAutoScroll(), { signal });
    this.#track.addEventListener('mouseup', () => this.#resumeAutoScrollAfterDelay(), { signal });

    let scrollTimeoutId;
    this.#track.addEventListener('scroll', () => {
      if (!this.#isMobile || this.#isTransitioning) return;
      clearTimeout(scrollTimeoutId);
      scrollTimeoutId = setTimeout(() => {
        const trackCenter = this.#track.scrollLeft + this.#track.offsetWidth / 2;
        let closestIndex = 0;
        let smallestDistance = Infinity;

        Array.from(this.#track.children).forEach((card, cardIndex) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(trackCenter - cardCenter);
          if (distance < smallestDistance) {
            smallestDistance = distance;
            closestIndex = cardIndex;
          }
        });

        this.#currentIndex = closestIndex;
        this.#updateActiveIndicator(closestIndex);
      }, FoundryTestimonialCarousel.SCROLL_TRACKING_DEBOUNCE_MS);
    }, { passive: true, signal });

    let resizeTimeoutId;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeoutId);
      resizeTimeoutId = setTimeout(() => this.#applyViewportLayout(), FoundryTestimonialCarousel.RESIZE_DEBOUNCE_MS);
    }, { signal });

    this.#reducedMotionMediaQuery.addEventListener('change', () => this.#applyViewportLayout(), { signal });
  }

  #applyViewportLayout() {
    if (this.#isMobile) {
      this.#cloneItemsForInfiniteLoop();
      this.#updateActiveIndicator(0);
      this.#scrollToTestimonial(0, false);
      this.#indicatorsContainer.style.display = 'flex';
      this.#startAutoScroll();
    } else {
      this.#track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
      this.#indicatorsContainer.style.display = 'none';
      this.#stopAutoScroll();
    }
  }
}

const carouselInstances = new WeakMap();

const mountCarousel = (section) => {
  if (!section.querySelector('[data-testimonials-track]')) return;
  if (carouselInstances.has(section)) return;
  carouselInstances.set(section, new FoundryTestimonialCarousel(section));
};

const unmountCarousel = (section) => {
  const instance = carouselInstances.get(section);
  if (!instance) return;
  instance.destroy();
  carouselInstances.delete(section);
};

const mountAll = () => {
  document.querySelectorAll('[data-section-id]').forEach(mountCarousel);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll, { once: true });
} else {
  mountAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('[data-section-id]') || event.target;
  mountCarousel(section);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('[data-section-id]') || event.target;
  unmountCarousel(section);
});
